import { createSelector } from '@reduxjs/toolkit';

import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplate } from 'dashboard/models/case-template';
import {
  ExternalIntegration,
  ExternalIntegrationStatus,
} from 'dashboard/models/external-integration';
import { Graph } from 'dashboard/models/graph';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { Identifier } from 'dashboard/models/identifier';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { List } from 'dashboard/models/list';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import { ThemeSet } from 'dashboard/models/theme-set';
import { TransactionStatus, TransactionStatusState } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import { User } from 'dashboard/models/user';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { Workflow, WorkflowActionMeta } from 'dashboard/models/workflow';
import { RootState } from 'dashboard/redux/store';
import { ApiModel } from 'lib/interfaces/api';
import { isCreateEnabledTemplate } from 'lib/utils/template';

const getAccountStatuses = (state: RootState): AccountStatus[] =>
  state.accountStatuses.accountStatuses;
const getAccountTypes = (state: RootState): AccountType[] => state.accountTypes.accountTypes;
const getCaseQueues = (state: RootState): CaseQueue[] => state.caseQueues.caseQueues;
const getCaseTemplates = (state: RootState): CaseTemplate[] => state.caseTemplates.caseTemplates;
const getExternalIntegrations = (state: RootState): ExternalIntegration[] =>
  state.externalIntegrations.externalIntegrations;
const getGraph = (state: RootState): Graph | null => state.graph.graph ?? null;
const getGraphQueryTemplates = (state: RootState): GraphQueryTemplate[] =>
  state.graphQueryTemplates.graphQueryTemplates;
const getInquiryTemplateVersion = (state: RootState): InquiryTemplateVersion =>
  state.inquiryTemplate.inquiryTemplateTreeData.inquiryTemplateVersion;
const getInquiryTemplates = (state: RootState): InquiryTemplate[] =>
  state.organization.inquiryTemplates;
const getLists = (state: RootState): List[] => state.lists.lists;
const getReportTemplates = (state: RootState): ReportTemplate[] =>
  state.reportTemplates.reportTemplates;
const getTags = (state: RootState): Tag[] => state.tags.tags.all;
const getTemplates = (state: RootState): Template[] => state.organization.templates;
const getThemes = (state: RootState): Theme[] => state.organization.themes;
const getThemeSets = (state: RootState): ThemeSet[] => state.themeSets.themeSets;
const getTransactionTypes = (state: RootState): TransactionType[] =>
  state.transactionTypes.transactionTypes;
const getTransactionStatuses = (state: RootState): TransactionStatus[] =>
  state.transactionStatuses.transactionStatuses;
const getUsers = (state: RootState): User[] => state.user.users;
const getVerificationTemplates = (state: RootState): VerificationTemplate[] =>
  state.verificationTemplates.verificationTemplates;
const getWorkflows = (state: RootState): Workflow[] =>
  state.workflows.workflows.filter((workflow) =>
    workflow.relationships.environments.data.some(
      (env) => env.id === state.currentEnvironment.currentEnvironment.id,
    ),
  );
export const getWorkflowActionMeta = createSelector(
  getAccountStatuses,
  getAccountTypes,
  getCaseQueues,
  getCaseTemplates,
  getExternalIntegrations,
  getGraph,
  getGraphQueryTemplates,
  getInquiryTemplateVersion,
  getInquiryTemplates,
  getLists,
  getReportTemplates,
  getTags,
  getTemplates,
  getThemes,
  getThemeSets,
  getTransactionStatuses,
  getTransactionTypes,
  getUsers,
  getVerificationTemplates,
  getWorkflows,
  (
    accountStatuses,
    accountTypes,
    caseQueues,
    caseTemplates,
    externalIntegrations,
    graph,
    graphQueryTemplates,
    inquiryTemplateVersion,
    inquiryTemplates,
    lists,
    reportTemplates,
    tags,
    templates,
    themes,
    themeSets,
    transactionStatuses,
    transactionTypes,
    users,
    verificationTemplates,
    workflows,
  ): WorkflowActionMeta => ({
    // Define this
    'account-identifiers': accountTypes.flatMap((accountType) => {
      return accountType.attributes['identifiers'].map((identifier: Identifier) => {
        return {
          templateToken: accountType.id,
          name: identifier['display-name'] || identifier['key'],
          displayName: identifier['display-name'] || identifier['key'],
          token: identifier['key'],
        };
      });
    }),
    'account-identifiers-unique': accountTypes.flatMap((accountType) => {
      return accountType.attributes['identifiers']
        .filter((identifier: Identifier) => identifier.config.unique)
        .map((identifier: Identifier) => {
          return {
            templateToken: accountType.id,
            name: identifier['display-name'] || identifier['key'],
            displayName: identifier['display-name'] || identifier['key'],
            token: identifier['key'],
          };
        });
    }),
    'account-statuses': accountStatuses.map((accountStatus) => ({
      token: accountStatus.id,
      name: accountStatus.attributes.name,
      displayName: accountStatus.attributes['display-name'],
      isArchived: !!accountStatus.attributes['archived-at'],
      templateToken: accountStatus.relationships['account-type'].data.id,
    })),
    'account-types': accountTypes.map(
      ({
        id: token,
        attributes: { name, 'field-schemas': fieldSchemas, 'relation-schemas': relationSchemas },
      }) => ({
        token,
        name,
        fieldSchemas,
        relationSchemas,
      }),
    ),
    'case-states': caseTemplates.flatMap((tmpl) =>
      tmpl.attributes.configuration.states.map((state) => ({
        token: state.token,
        name: state.name,
        displayName: `${state.name} (${tmpl.attributes['case-prefix']})`,
        templateToken: tmpl.id,
      })),
    ),
    'case-templates': caseTemplates.map((tmpl) => ({
      token: tmpl.id,
      status: tmpl.attributes.status,
      name: tmpl.attributes.name,
      prefix: tmpl.attributes['case-prefix'],
      resolutions: tmpl.attributes.configuration.resolutions, // TODO(zixiao): cleanup resolutions
      states: tmpl.attributes.configuration.states,
      ['assignment-policies']: tmpl.attributes.configuration['assignment-policies'],
      ['field-schema']: tmpl.attributes.configuration['field-schema'],
      // (evan.qi) added for consistency amongst action meta templates that store field schemas
      fieldSchemas: tmpl.attributes.configuration['field-schema'],
      ['case-message-templates']: tmpl.attributes.configuration['case-message-templates'],
    })),
    'case-queues': caseQueues.map((cqueue) => ({
      token: cqueue.id,
      name: cqueue.attributes.name,
    })),
    'external-integrations': externalIntegrations.map((integration) => ({
      token: integration.id,
      name: integration.attributes.name,
      type: integration.type,
      isArchived: integration.attributes.status === ExternalIntegrationStatus.Archived,
    })),
    graph: graph,
    'graph-query-templates': graphQueryTemplates.filter((gqt) =>
      Object.prototype.hasOwnProperty.call(gqt.attributes.payload, 'variables'),
    ),
    'inquiry-templates': inquiryTemplates.filter(isCreateEnabledTemplate).map(
      ({
        id,
        attributes: { 'name-display': nameDisplay },
        relationships: {
          'latest-published-version': { data: latestPublishedVersion },
        },
      }) => ({
        token: id,
        name: nameDisplay,
        latestPublishedVersion: latestPublishedVersion?.id as string,
      }),
    ),
    'inquiry-template-version': {
      token: inquiryTemplateVersion?.id,
      steps: inquiryTemplateVersion?.attributes.steps.map((step) => ({
        name: step.name,
      })),
    },
    lists: lists?.map((list) => ({
      name: list.attributes.name,
      id: list.id,
      token: list.id,
      type: list.type,
      attributes: list.attributes,
    })),
    'report-templates': reportTemplates
      .filter((tmpl) =>
        [
          ApiModel.ReportTemplateAddressLookup,
          ApiModel.ReportTemplateAdverseMedia,
          ApiModel.ReportTemplateBusinessAdverseMedia,
          ApiModel.ReportTemplateBusinessAssociatedPersons,
          ApiModel.ReportTemplateBusinessClassification,
          ApiModel.ReportTemplateBusinessIndustryClassification,
          ApiModel.ReportTemplateBusinessRegistrationsLookup,
          ApiModel.ReportTemplateBusinessLiensLookup,
          ApiModel.ReportTemplateBusinessLookup,
          ApiModel.ReportTemplateBusinessNonprofit,
          ApiModel.ReportTemplateBusinessOnlinePresence,
          ApiModel.ReportTemplateBusinessSocialMedia,
          ApiModel.ReportTemplateBusinessWatchlist,
          ApiModel.ReportTemplateBusinessWebsite,
          ApiModel.ReportTemplateCryptoAddressWatchlist,
          ApiModel.ReportTemplateCustomList,
          ApiModel.ReportTemplateEmailAddress,
          ApiModel.ReportTemplatePhoneNumber,
          ApiModel.ReportTemplatePoliticallyExposedPerson,
          ApiModel.ReportTemplateProfile,
          ApiModel.ReportTemplateProfileNonAuthoritative,
          ApiModel.ReportTemplateScreening,
          ApiModel.ReportTemplateSocialMedia,
          ApiModel.ReportTemplateSynthetic,
          ApiModel.ReportTemplateWatchlist,
        ].includes(tmpl.type),
      )
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    [ApiModel.ReportTemplateEquifaxOneview]: reportTemplates
      .filter((tmpl) => tmpl.type === ApiModel.ReportTemplateEquifaxOneview)
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    [ApiModel.ReportTemplateKyckrBusinessLookup]: reportTemplates
      .filter((tmpl) => tmpl.type === ApiModel.ReportTemplateKyckrBusinessLookup)
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    [ApiModel.ReportTemplateMiddeskBusinessLookup]: reportTemplates
      .filter((tmpl) => tmpl.type === ApiModel.ReportTemplateMiddeskBusinessLookup)
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    [ApiModel.ReportTemplateSentilinkApplicationRisk]: reportTemplates
      .filter((tmpl) => tmpl.type === ApiModel.ReportTemplateSentilinkApplicationRisk)
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    [ApiModel.ReportTemplateSentilinkScores]: reportTemplates
      .filter((tmpl) => tmpl.type === ApiModel.ReportTemplateSentilinkScores)
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    [ApiModel.ReportTemplateChainalysisAddressScreening]: reportTemplates
      .filter((tmpl) => tmpl.type === ApiModel.ReportTemplateChainalysisAddressScreening)
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    [ApiModel.ReportTemplateMxAccount]: reportTemplates
      .filter((tmpl) => tmpl.type === ApiModel.ReportTemplateMxAccount)
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    [ApiModel.ReportTemplateTrmWalletScreening]: reportTemplates
      .filter((tmpl) => tmpl.type === ApiModel.ReportTemplateTrmWalletScreening)
      .map((tmpl) => ({
        token: tmpl.id,
        name: tmpl.attributes.name,
        type: tmpl.type,
        isArchived: tmpl.attributes.archived,
      })),
    tags: tags.map((tag) => ({
      token: tag.id,
      name: tag.attributes.name,
    })),
    templates: templates.map((tmpl) => ({
      token: tmpl.id,
      name: tmpl.attributes.name,
    })),
    themes: themes.map((theme) => ({
      token: theme.id,
      name: theme.attributes.name,
    })),
    'theme-sets': themeSets.map((themeSet) => ({
      token: themeSet.id,
      name: themeSet.attributes.name,
    })),
    'transaction-identifiers': transactionTypes.flatMap((transactionType) => {
      return (transactionType.attributes['identifiers'] || []).map((identifier: Identifier) => {
        return {
          templateToken: transactionType.id,
          displayName: identifier['display-name'] || identifier['key'],
          name: identifier['display-name'] || identifier['key'],
          token: identifier['key'],
        };
      });
    }),
    'transaction-identifiers-unique': transactionTypes.flatMap((transactionType) => {
      return (transactionType.attributes['identifiers'] || [])
        .filter((identifier) => identifier.config.unique)
        .map((identifier: Identifier) => {
          return {
            templateToken: transactionType.id,
            displayName: identifier['display-name'] || identifier['key'],
            name: identifier['display-name'] || identifier['key'],
            token: identifier['key'],
          };
        });
    }),
    'transaction-types': transactionTypes.map(
      ({
        attributes: { name, 'relation-schemas': relationSchemas, 'field-schemas': fieldSchemas },
        id,
      }) => ({
        name,
        token: id,
        relationSchemas,
        fieldSchemas,
      }),
    ),
    'transaction-statuses': transactionStatuses.map((transactionStatus) => ({
      token: transactionStatus.id,
      name: transactionStatus.attributes.name,
      displayName: transactionStatus.attributes['display-name'],
      isActive: transactionStatus.attributes['status'] === TransactionStatusState.Active,
      transactionType: transactionStatus.relationships['transaction-type'].data.id,
      templateToken: transactionStatus.relationships['transaction-type'].data.id,
    })),
    users: users.map((user) => ({
      token: user.id,
      name: userDisplayName(user) ?? '',
    })),
    'verification-templates': verificationTemplates.map((tmpl) => ({
      token: tmpl.id,
      name: tmpl.attributes.name,
      type: tmpl.type,
    })),
    'verification-template-versions': verificationTemplates
      .filter(
        (template) =>
          template.relationships['latest-draft-version']?.data != null ||
          template.relationships['latest-published-version']?.data != null,
      )
      .map(
        ({
          id: verificationTemplateId,
          relationships: {
            'latest-draft-version': latestDraftVersion,
            'latest-published-version': latestPublishedVersion,
          },
          attributes: { name },
        }) => {
          const latestVersion =
            latestDraftVersion?.data != null
              ? latestDraftVersion.data
              : latestPublishedVersion.data;
          return {
            name,
            type: latestVersion?.type as ApiModel.VerificationTemplateVersion,
            token: latestVersion?.id as string,
            verificationTemplateId: verificationTemplateId,
          };
        },
      ),
    workflows: workflows?.map((workflow) => ({
      name: workflow.attributes.name,
      id: workflow.id,
      token: workflow.id,
      ['trigger-type']: workflow.attributes['trigger-type'],
      status: workflow.attributes['status'],
    })),
  }),
);
